import React, { useEffect, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Loader2, X } from 'lucide-react';
import ProjectGalleryCarousel from '@/components/projects/ProjectGalleryCarousel';
import ProjectCoverMedia from '@/components/projects/ProjectCoverMedia';
import MediaDisplay from '@/components/media/MediaDisplay';
import SEO from '../components/SEO';
import { useLanguage } from '@/i18n/LanguageProvider';

const demoProject = {
  id: 'demo-1',
  title: 'Casa Moderna',
  category: 'residential',
  year: '2024',
  location: 'Milan, Italy',
  client: 'Private',
  area: '280 sqm',
  photographer: 'Studio Photography',
  cover_image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=1920&q=80',
  gallery_images: [
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80',
  'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200&q=80',
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80',
  'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1200&q=80'],

  description: `This residential project represents the perfect balance between modern aesthetics and functional living. 
  
The intervention involved a complete restructuring of the existing space, creating an open-plan living area that maximizes natural light and creates seamless connections between indoor and outdoor spaces.

The materials have been carefully selected to enhance the interplay of light and texture, with warm oak flooring complementing the clean white walls and black steel accents. Custom millwork throughout the home provides both storage solutions and architectural interest.

The kitchen features a minimalist design with integrated appliances and a dramatic waterfall island that serves as the heart of the home. Floor-to-ceiling windows in the living areas frame views of the surrounding landscape, blurring the boundaries between interior and exterior.

Each bedroom is designed as a private retreat, with thoughtfully positioned windows that capture the best light throughout the day. The master suite includes a spa-like bathroom with natural stone and custom bronze fixtures.`
};

export default function ProjectDetail() {
  const { t } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);
  const urlParams = new URLSearchParams(window.location.search);
  const projectId = urlParams.get('id');
  const category = urlParams.get('category');
  const backUrl = category 
    ? `${createPageUrl('Projects')}?category=${category}`
    : createPageUrl('Projects');

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setSelectedImage(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      if (projectId?.startsWith('demo-')) {
        return demoProject;
      }
      const projects = await base44.entities.Project.filter({ id: projectId });
      return projects[0] || demoProject;
    },
    enabled: !!projectId
  });

  if (isLoading) {
    return (
      <div className="pt-32 flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#74675a]" />
      </div>);

  }

  if (!project) {
    return (
      <div className="pt-32 text-center min-h-screen">
        <p className="text-[#5f5f5f]">{t('Project not found')}</p>
        <Link to={createPageUrl('Projects')} className="text-[#74675a] hover:underline mt-4 inline-block">
          {t('Back to Projects')}
        </Link>
      </div>);

  }

  const details = [
  { label: t('Anno'), value: project.year },
  { label: t('Luogo'), value: project.location },
  { label: t('Tipologia'), value: t(project.category?.charAt(0).toUpperCase() + project.category?.slice(1)) },
  { label: t('Cliente'), value: project.client },
  { label: t('Superficie'), value: project.area },
  { label: t('Foto'), value: project.photographer }].
  filter((d) => d.value);

  const projectDescription = project.short_description || project.description || `Progetto ${project.category} realizzato da Bellomonte & Pensabene a ${project.location}`;

  return (
    <>
      <SEO 
        title={project.title}
        description={projectDescription.substring(0, 160)}
        keywords={`progetto ${project.category}, ${project.location}, architettura, interior design`}
        image={project.cover_image}
        type="article"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "CreativeWork",
          "name": project.title,
          "description": projectDescription,
          "image": project.cover_image,
          "author": {
            "@type": "Organization",
            "name": "Bellomonte & Pensabene Studio di Architettura"
          },
          "datePublished": project.year,
          "locationCreated": project.location
        }}
      />
      <div>
      {/* Image Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors"
            >
              <X size={32} />
            </button>
            <img
              src={selectedImage}
              alt={t('Ingrandimento')}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Image */}
      <div className="relative h-[60vh] md:h-[80vh] overflow-hidden">
        <motion.div initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.2 }} className="h-full w-full">
          <ProjectCoverMedia project={project} className="h-full w-full object-cover" />
        </motion.div>

        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-20">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-white text-3xl md:text-5xl lg:text-6xl font-serif font-light">

            {project.title}
          </motion.h1>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-[1800px] px-6 py-12 md:px-12 md:py-24 lg:px-20">
        {/* Back Link */}
        <div className="mb-12">
          <Link
            to={backUrl}
            className="inline-flex items-center gap-2 text-[#5f5f5f] hover:text-[#2d2d2d] 
              transition-colors text-sm">


            <ArrowLeft size={16} />
            {t('Torna ai Progetti')}
          </Link>
        </div>

        {/* Title and Details */}
        <div           className="mb-12 grid grid-cols-1 gap-8 md:mb-24 lg:grid-cols-2 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}>

            <h2 className="font-serif text-3xl md:text-4xl text-[#2d2d2d] mb-6">
              {project.title}
            </h2>
            <div className="w-16 h-px bg-[#74675a]" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-2 gap-6">

            {details.map((detail) =>
            <div key={detail.label}>
                <p className="text-xs text-[#74675a] uppercase tracking-wider mb-1">
                  {detail.label}
                </p>
                <p className="text-[#2d2d2d]">{detail.value}</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Description */}
        {project.description &&
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="max-w-3xl mb-8 md:mb-12">

            <p className="whitespace-pre-line text-[17px] leading-[1.75] text-[#5f5f5f] md:text-lg">
              {project.description}
            </p>
          </motion.div>
        }

        {/* Before/After Plans */}
        {(project.before_plan || project.after_plan) &&
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-16 md:mb-24">

            <h3 className="text-xs tracking-widest-custom uppercase text-[#74675a] mb-8 text-center">
              {t('Planimetrie')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {project.before_plan &&
            <div className="cursor-pointer" onClick={() => setSelectedImage(project.before_plan)}>
                  <img
                src={project.before_plan}
                alt={t('Planimetria prima')}
                className="w-full h-auto object-contain" />

                </div>
            }
              {project.after_plan &&
            <div className="cursor-pointer" onClick={() => setSelectedImage(project.after_plan)}>
                  <img
                src={project.after_plan}
                alt={t('Planimetria dopo')}
                className="w-full h-auto object-contain" />

                </div>
            }
            </div>
          </motion.div>
        }

        {/* Gallery Carousel */}
        {project.gallery_images && project.gallery_images.length > 0 &&
          <ProjectGalleryCarousel
            media={project.gallery_images}
            mediaTypes={project.gallery_media_types || []}
            mediaMeta={project.gallery_images_meta || []}
            projectTitle={project.title}
          />
        }

        {/* Moodboard Section */}
        {(project.moodboard_image || project.moodboard_text) &&
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16 md:mb-24">

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {project.moodboard_image &&
            <div className="w-full flex items-center justify-center cursor-pointer" onClick={() => setSelectedImage(project.moodboard_image)}>
                  <img
                src={project.moodboard_image}
                alt="Moodboard"
                className="w-full h-auto object-contain" />

                </div>
            }
              {project.moodboard_text &&
            <div>
                  <h3 className="text-xs tracking-widest-custom uppercase text-[#74675a] mb-6">
                    Moodboard
                  </h3>
                  <p className="whitespace-pre-line text-[17px] leading-[1.75] text-[#5f5f5f] md:text-lg">
                    {project.moodboard_text}
                  </p>
                </div>
            }
            </div>
          </motion.div>
        }

        {/* Client Video Section */}
        {(project.client_video_url || project.client_video_description) &&
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16 md:mb-24">

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {project.client_video_description &&
            <div>
                  <h3 className="text-xs tracking-widest-custom uppercase text-[#74675a] mb-6">
                    {t('La Voce del Cliente')}
                  </h3>
                  <p className="whitespace-pre-line text-[17px] leading-[1.75] text-[#5f5f5f] md:text-lg">
                    {project.client_video_description}
                  </p>
                </div>
            }
              {project.client_video_url &&
            <div className="aspect-[9/16] max-h-[600px] overflow-hidden bg-black">
                  <MediaDisplay type="video" url={project.client_video_url} meta={project.client_video_meta || { title: `Video cliente - ${project.title}`, description: project.client_video_description }} controls className="w-full h-full object-contain" />
                </div>
            }
            </div>
          </motion.div>
        }
      </div>
    </div>
    </>
  );
}